import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, Phone, Mail, Building, MessageSquare, 
  Download, ChevronRight, Plus, Search, Filter,
  CheckCircle, XCircle, Clock, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'bg-blue-500', icon: Plus },
  contacted: { label: 'Contactado', color: 'bg-yellow-500', icon: Phone },
  qualified: { label: 'Cualificado', color: 'bg-purple-500', icon: CheckCircle },
  proposal: { label: 'Propuesta', color: 'bg-orange-500', icon: MessageSquare },
  won: { label: 'Ganado', color: 'bg-green-500', icon: CheckCircle },
  lost: { label: 'Perdido', color: 'bg-red-500', icon: XCircle }
};

export default function CRMPage() {
  const { t } = useTranslation();
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/auth');
      return;
    }
    fetchLeads();
    fetchStats();
  }, [isAdmin, navigate]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Estado actualizado');
        fetchLeads();
        fetchStats();
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status: newStatus });
        }
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const addNote = async (leadId) => {
    if (!newNote.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ note: newNote })
      });
      
      if (response.ok) {
        toast.success('Nota añadida');
        setNewNote('');
        fetchLeads();
        // Refresh selected lead
        const updatedLead = leads.find(l => l.id === leadId);
        if (updatedLead) {
          const res = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });
          if (res.ok) setSelectedLead(await res.json());
        }
      }
    } catch (error) {
      toast.error('Error al añadir nota');
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/export/excel`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads_crm_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Excel descargado');
      }
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">{t('crm.title')}</h1>
            <p className="text-slate-400 mt-1">Gestión de contactos y oportunidades</p>
          </div>
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            {t('crm.exportExcel')}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Leads</p>
                    <p className="text-2xl font-bold text-white">{stats.total_leads}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">{t('crm.thisMonth')}</p>
                    <p className="text-2xl font-bold text-white">{stats.leads_this_month}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">{t('crm.won')}</p>
                    <p className="text-2xl font-bold text-green-400">{stats.by_status?.won || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">{t('crm.conversionRate')}</p>
                    <p className="text-2xl font-bold text-white">{stats.conversion_rate}%</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre, email o empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700 text-white"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-md px-4 py-2 text-white"
          >
            <option value="all">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="lg:col-span-2 space-y-4">
            {filteredLeads.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center text-slate-400">
                  No se encontraron leads
                </CardContent>
              </Card>
            ) : (
              filteredLeads.map(lead => (
                <Card 
                  key={lead.id} 
                  className={`bg-slate-800/50 border-slate-700 cursor-pointer transition-all hover:border-amber-500/50 ${selectedLead?.id === lead.id ? 'border-amber-500' : ''}`}
                  onClick={() => setSelectedLead(lead)}
                  data-testid={`lead-card-${lead.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <span className="text-amber-500 font-semibold">
                            {lead.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{lead.name}</h3>
                          <p className="text-slate-400 text-sm">{lead.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`${STATUS_CONFIG[lead.status || 'new']?.color} text-white`}>
                          {STATUS_CONFIG[lead.status || 'new']?.label}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                    {lead.company && (
                      <div className="mt-2 flex items-center text-slate-400 text-sm">
                        <Building className="w-4 h-4 mr-1" />
                        {lead.company}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Lead Details */}
          <div className="lg:col-span-1">
            {selectedLead ? (
              <Card className="bg-slate-800/50 border-slate-700 sticky top-6">
                <CardHeader>
                  <CardTitle className="text-white">{t('crm.leadDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center text-slate-300">
                      <Mail className="w-4 h-4 mr-2 text-slate-400" />
                      {selectedLead.email}
                    </div>
                    {selectedLead.phone && (
                      <div className="flex items-center text-slate-300">
                        <Phone className="w-4 h-4 mr-2 text-slate-400" />
                        {selectedLead.phone}
                      </div>
                    )}
                    {selectedLead.company && (
                      <div className="flex items-center text-slate-300">
                        <Building className="w-4 h-4 mr-2 text-slate-400" />
                        {selectedLead.company}
                      </div>
                    )}
                  </div>

                  {selectedLead.message && (
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="text-sm text-slate-300">{selectedLead.message}</p>
                    </div>
                  )}

                  {/* Status Change */}
                  <div>
                    <p className="text-sm text-slate-400 mb-2">{t('crm.changeStatus')}</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <button
                          key={key}
                          onClick={() => updateLeadStatus(selectedLead.id, key)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                            selectedLead.status === key
                              ? `${config.color} text-white`
                              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                        >
                          {config.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <p className="text-sm text-slate-400 mb-2">{t('crm.notes')}</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                      {selectedLead.notes?.map((note, idx) => (
                        <div key={idx} className="p-2 bg-slate-700/50 rounded text-sm">
                          <p className="text-slate-300">{note.text}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {note.author} - {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Añadir nota..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-white text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && addNote(selectedLead.id)}
                      />
                      <Button 
                        onClick={() => addNote(selectedLead.id)} 
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-700">
                    Creado: {new Date(selectedLead.created_at).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center text-slate-400">
                  Selecciona un lead para ver detalles
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
